import Button from 'components/Form/Button';
import { Check } from 'components/Form/Check';
import FormCore from 'components/Form/Core';
import {
  ActionBar,
  ActionButtons,
  ActionTotal,
  Cell,
  CellNote,
  InstallmentList,
  ListFallback,
  SettlementContainer,
  SettlementHint,
  WarningBox,
  WarningText,
  WarningTitle,
} from 'components/Modal/HistoricoCobranca/components/Baixa/styled';
import moment from 'moment';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ReverseSettlement } from 'services/cobranca';
import { EmptyMessage } from 'ui/styled';
import { exposeStrapiError } from 'utils';
import { CANCELLED_STATUS, installmentLabel } from 'utils/billing';
import { parseCurrency } from 'utils/parsers';
import { ReasonField, ReversalHeader, ReversalRow } from './styled';

// Quem estorna. Comparado com `true` estrito: o administrador nao dispensa a
// flag, o backend devolve 403 para admin sem ela.
export const canReverseSettlement = (user) => user?.estorna_baixa === true;

const TYPE_LABELS = {
  normal: 'Pagamento',
  renegociado: 'Renegociação',
  cancelado: 'Cancelamento da renegociação',
};

const toNumber = (value) => {
  const number = Number(value);
  return value === null || value === undefined || value === '' || isNaN(number)
    ? null
    : number;
};

const plural = (n, singular, pluralForm) => (n === 1 ? singular : pluralForm);

const formatDate = (value) =>
  value
    ? moment(`${value}`.slice(0, 10), 'YYYY-MM-DD').format('DD/MM/YYYY')
    : '-';

// Por que o titulo baixado nao pode ser estornado aqui, ou `null`. Espelha as
// regras do backend, que confere tudo de novo e devolve 400: so baixa com
// `tipo_baixa: normal` (manual, lote ou arquivo Omni) volta. A renegociacao se
// desfaz pelo cancelamento dela, e nao pelo estorno.
const lockReason = (titulo, cobrancaStatus) => {
  if (cobrancaStatus === CANCELLED_STATUS) {
    return 'Cobrança cancelada: não aceita estorno';
  }
  if (titulo?.tipo_baixa === 'renegociado' || titulo?.renegociar === true) {
    return 'Baixado pela renegociação: cancele a renegociação para desfazer';
  }
  if (titulo?.tipo_baixa === 'cancelado') {
    return 'Fechado pelo cancelamento da renegociação';
  }
  if (titulo?.tipo_baixa && titulo.tipo_baixa !== 'normal') {
    return 'Tipo de baixa não aceita estorno';
  }
  return null;
};

// O 403 desta rota e falta da flag `estorna_baixa`, e nao sessao expirada.
const handleError = (result) => {
  if (result && result?.error?.status === 403) {
    toast.error(
      result?.error?.message || 'Você não tem permissão para estornar baixas.'
    );
    return true;
  }
  if (exposeStrapiError(result)) return true;
  if (!Array.isArray(result?.titulos_estornados)) {
    toast.error('O estorno não devolveu os títulos estornados.');
    return true;
  }
  return false;
};

// Estorno de baixa, dentro da aba Baixa do modal de Historico: lista os
// titulos baixados da cobranca e desfaz a baixa dos escolhidos, com
// justificativa obrigatoria e confirmacao. Os que nao podem ser estornados
// aparecem travados, com o motivo. `onReversed` roda depois do estorno, para
// reler os titulos e recarregar a linha do tempo e a listagem.
export const SettlementReversal = ({
  cobrancaId,
  cobrancaStatus,
  titulos,
  onReversed,
}) => {
  const formRef = useRef();
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState('');
  // edit -> confirm -> saving
  const [step, setStep] = useState('edit');
  const saving = step === 'saving';

  const items = useMemo(
    () =>
      (titulos || [])
        .filter((titulo) => titulo?.status === 'baixado')
        .map((titulo) => ({
          parcela: titulo?.parcela,
          label: installmentLabel(titulo),
          entry: titulo?.entrada === true,
          dueDate: titulo?.data_vencimento,
          settledAt: titulo?.data_baixa,
          value: toNumber(titulo?.valor),
          received: toNumber(titulo?.valor_recebido),
          discount: toNumber(titulo?.valor_desconto),
          type: TYPE_LABELS[titulo?.tipo_baixa] || titulo?.tipo_baixa || '-',
          lock: lockReason(titulo, cobrancaStatus),
        }))
        .sort((a, b) => (a.parcela || 0) - (b.parcela || 0)),
    [titulos, cobrancaStatus]
  );

  const chosen = items.filter(
    (item) => !item.lock && selected.includes(item.parcela)
  );
  const total = chosen.reduce((sum, item) => sum + (item.received || 0), 0);
  const entryChosen = chosen.some((item) => item.entry);

  const toggle = (parcela) =>
    setSelected((current) =>
      current.includes(parcela)
        ? current.filter((item) => item !== parcela)
        : [...current, parcela]
    );

  const review = () => {
    // getForm() valida a justificativa obrigatoria e emite o toast sozinho.
    const form = formRef.current?.getForm();
    const text = `${form?.justificativa || ''}`.trim();
    if (!form) return;
    if (!text) {
      toast.error('Informe a justificativa do estorno.');
      return;
    }
    setReason(text);
    setStep('confirm');
  };

  const reverse = async () => {
    if (saving) return;
    setStep('saving');
    const result = await ReverseSettlement(cobrancaId, {
      data: {
        parcelas: chosen.map((item) => item.parcela),
        justificativa: reason,
      },
    });
    if (handleError(result)) {
      setStep('confirm');
      return;
    }

    const reversed = result.titulos_estornados;
    toast.success(
      `${reversed.length} ${plural(
        reversed.length,
        'baixa estornada',
        'baixas estornadas'
      )}. Atraso e mora foram recalculados até hoje.`
    );
    // Na entrada o `mora_recalculada: false` e esperado (ela nao tem mora);
    // nas demais, o banco nao tem formula de juros.
    const notRecalculated = reversed.filter(
      (item) => item?.mora_recalculada === false && item?.parcela !== 0
    );
    if (notRecalculated.length) {
      toast.info(
        `O banco não tem fórmula de juros cadastrada: a mora ${plural(
          notRecalculated.length,
          'da parcela',
          'das parcelas'
        )} ${notRecalculated
          .map((item) => item?.parcela)
          .join(', ')} ficou a que estava gravada.`
      );
    }

    setSelected([]);
    setReason('');
    setStep('edit');
    onReversed?.(result);
  };

  if (!items.length) {
    return (
      <ListFallback>
        <EmptyMessage>Nenhum título baixado neste contrato.</EmptyMessage>
      </ListFallback>
    );
  }

  return (
    <SettlementContainer>
      <SettlementHint>
        O estorno desfaz a baixa lançada errado: o título volta para vencido ou
        a vencer, e atraso, juros, multa e IOF são recalculados até hoje. A
        régua volta só com os avisos que ainda não passaram. Baixas feitas pela
        renegociação não são estornadas aqui: para desfazê-las, cancele a
        renegociação.
      </SettlementHint>

      {step === 'edit' ? (
        <>
          <ReversalHeader>
            <span />
            <span>Parcela</span>
            <span>Vencimento</span>
            <span>Data da baixa</span>
            <span>Valor</span>
            <span>Desconto</span>
            <span>Recebido</span>
            <span>Tipo</span>
          </ReversalHeader>
          <InstallmentList>
            {items.map((item) => {
              const isSelected = !item.lock && selected.includes(item.parcela);
              return (
                <ReversalRow
                  key={item.parcela}
                  $selected={isSelected}
                  $locked={!!item.lock}
                  title={item.lock || undefined}
                >
                  {item.lock ? (
                    <span />
                  ) : (
                    <Check
                      checked={isSelected}
                      onChange={() => toggle(item.parcela)}
                    />
                  )}
                  <Cell>{item.label}</Cell>
                  <Cell>{formatDate(item.dueDate)}</Cell>
                  <Cell>{formatDate(item.settledAt)}</Cell>
                  <Cell>
                    {item.value === null ? '-' : parseCurrency(item.value)}
                  </Cell>
                  <Cell>
                    {item.discount ? parseCurrency(item.discount) : '-'}
                  </Cell>
                  <Cell>
                    {item.received === null
                      ? '-'
                      : parseCurrency(item.received)}
                  </Cell>
                  <Cell>
                    {item.type}
                    {item.lock ? <CellNote>{item.lock}</CellNote> : null}
                  </Cell>
                </ReversalRow>
              );
            })}
          </InstallmentList>
          <ReasonField>
            <FormCore
              ref={formRef}
              formItems={[
                {
                  ref: 'justificativa',
                  placeholder: 'Justificativa do estorno',
                  type: 'text',
                  full: true,
                  multiline: true,
                  maxRows: 3,
                  required: true,
                },
              ]}
              register={{ justificativa: reason }}
            />
          </ReasonField>
        </>
      ) : (
        <WarningBox>
          <WarningTitle>Confirme o estorno</WarningTitle>
          <WarningText>
            {chosen
              .map(
                (item) =>
                  `${item.entry ? 'Entrada' : `Parcela ${item.parcela}`}: ${
                    item.received === null ? '-' : parseCurrency(item.received)
                  } baixado em ${formatDate(item.settledAt)}`
              )
              .join('  |  ')}
          </WarningText>
          <WarningText>
            {chosen.length}{' '}
            {plural(
              chosen.length,
              'baixa será estornada',
              'baixas serão estornadas'
            )}
            , somando {parseCurrency(total)} recebidos. Justificativa: {reason}
          </WarningText>
          {entryChosen ? (
            <WarningText>
              <strong>Entrada da renegociação:</strong> sem a entrada paga, a
              renegociação é cancelada automaticamente no 3º dia útil depois do
              vencimento da entrada.
            </WarningText>
          ) : null}
        </WarningBox>
      )}

      <ActionBar>
        <ActionTotal>
          {chosen.length
            ? `${chosen.length} ${plural(
                chosen.length,
                'baixa selecionada',
                'baixas selecionadas'
              )} · ${parseCurrency(total)}`
            : 'Selecione as baixas a estornar'}
        </ActionTotal>
        <ActionButtons>
          {step === 'edit' ? (
            <Button
              color="error"
              onClick={review}
              disabled={!chosen.length}
              nospace
            >
              Estornar
            </Button>
          ) : (
            <>
              <Button
                color="lightgrey"
                onClick={() => setStep('edit')}
                disabled={saving}
                nospace
              >
                Voltar
              </Button>
              <Button color="error" onClick={reverse} disabled={saving} nospace>
                {saving ? 'Estornando...' : 'Confirmar estorno'}
              </Button>
            </>
          )}
        </ActionButtons>
      </ActionBar>
    </SettlementContainer>
  );
};
